import { useTranslation } from '@shared/core/i18n';
import { useMemo } from 'react';
import { SearchParams } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { formatScheduleItemDate } from '@localization/formatters/scheduleFormatter';
import { getTimeZoneAbbr } from './ScheduleList.utils';
import { useEventLocale } from '@localization/context/LocaleContext';

type RouteBuilder = Readonly<{
  buildRelativeUrl: (eventHandle: string, searchParams: SearchParams) => string;
}>;

export type ScheduleListTranslations = Readonly<{
  addToCalendar: string;
  whereToStay: string;
  dressCode: string;
  getDirections: string;
  moreDetails: string;
  timeframeTz: (showTZ: boolean, startDateTime?: number, endDateTime?: number, timezone?: string) => string | undefined;
  timeTz: (time?: string, timezoneAbbr?: string) => string | undefined;
  description: (
    bookYourStay: RouteBuilder,
    description?: string | null,
    dressCode?: string | null,
    eventHandle?: string | null,
    clientUri?: string | null,
    includeAccommodationsLink?: boolean,
  ) => string | undefined;
  toBeAnnounced: string;
  toBeAnnouncedDescription: string;
}>;

export const useTranslations = (): ScheduleListTranslations => {
  const { tq } = useTranslation('guestKit');
  const { language } = useEventLocale();

  const value = useMemo<ScheduleListTranslations>(() => {
    return {
      addToCalendar: tq`Add to Calendar`,
      whereToStay: tq`Where to Stay`,
      dressCode: tq`Dress Code`,
      getDirections: tq`Directions`,
      moreDetails: tq`For more details`,
      description: (bookYourStay, description, dressCode, eventHandle, clientUri, includeAccommodationsLink = true) => {
        const contentParts: string[] = [];
        if (description) contentParts.push(description);
        if (dressCode) contentParts.push(`${tq`Dress Code`}: ${dressCode}`);

        if (!eventHandle || !clientUri) {
          return contentParts.length > 0 ? contentParts.join('\n\n') : undefined;
        }

        const normalizedClientUri = clientUri.endsWith('/') ? clientUri.slice(0, -1) : clientUri;
        const scheduleUrl = new URL(`${normalizedClientUri}/${eventHandle}/schedule`);
        scheduleUrl.searchParams.set('utm_medium', 'calendar');
        scheduleUrl.searchParams.set('utm_campaign', 'schedule_item');
        scheduleUrl.searchParams.set('utm_source', 'schedule_list_item');

        const linkParts: string[] = [`${tq`For more details`}:\n${scheduleUrl.toString()}`];

        if (includeAccommodationsLink) {
          const accommodationsUrl =
            normalizedClientUri +
            bookYourStay.buildRelativeUrl(eventHandle, {
              utm_medium: 'calendar',
              utm_campaign: 'schedule_item',
              utm_source: 'schedule_list_item',
            });
          linkParts.push(`${tq`Where to Stay`}:\n${accommodationsUrl}`);
        }

        const allParts = [...contentParts, linkParts.join('\n\n')];
        return allParts.join('\n\n');
      },
      timeframeTz: (showTZ: boolean, startDateTime?: number, endDateTime?: number, timezone?: string): string | undefined => {
        // Note: timeframeTz is a callback function called with dynamic dates, so we can't use hooks here.
        // We use formatScheduleItemDate utility which accepts language from context.
        const start = startDateTime ? formatScheduleItemDate(new Date(startDateTime), language, timezone) : undefined;

        if (!start) return undefined;

        const end = endDateTime ? formatScheduleItemDate(new Date(endDateTime), language, timezone) : undefined;

        const timezoneAbbr = getTimeZoneAbbr(startDateTime, timezone);

        // Single time only - return time with or without timezone
        if (!end) {
          return showTZ ? tq`${start.time} (${timezoneAbbr})` : start.time;
        }

        // Check if multi-day event by comparing date strings
        const isMultiDay = start.date !== end.date || start.year !== end.year;

        if (isMultiDay) {
          return showTZ ? tq`${start.date} ${start.time} to\n${end.date} ${end.time} (${timezoneAbbr})` : tq`${start.date} ${start.time} to\n${end.date} ${end.time}`;
        }

        return showTZ ? tq`${start.time} to ${end.time} (${timezoneAbbr})` : tq`${start.time} to ${end.time}`;
      },
      timeTz: (time, timezoneAbbr) => tq`${time} (${timezoneAbbr})`,
      toBeAnnounced: tq`To Be Announced`,
      toBeAnnouncedDescription: tq`Details for this event will be shared at a later date.`,
    };
  }, [tq, language]);

  return value;
};
